import { useEffect, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Person } from "api/Community/CommunityInterfaces";

import GenericTable, { Column } from "components/Data/DataDisplays/GenericTable";
import MoveConfirmDialog from "components/Flow/Tabs/ItemMovementTabs/MoveConfirmDialog";
import DiscipleshipPeopleControlPanel from "./DiscipleshipPeopleControlPanel";

interface DiscipleshipPeopleMovementTabProps {
  nodeData: any | null;
  nameOfFunctionalityField?: string;
  getNodePeople: (nodeId: number) => Promise<Person[]>;
  movePeopleBetweenNodes: (payload: any) => Promise<any>;

  inputTypeField?: string;
  outputTypeField?: string;
  inputLabelField?: string;
  outputLabelField?: string;

  inputFilterField?: string;
  outputFilterField?: string;

  includeMovementControls?: boolean;
}

export default function DiscipleshipPeopleMovementTab({
  nodeData,
  nameOfFunctionalityField = "functionalities",
  getNodePeople,
  movePeopleBetweenNodes,
  inputTypeField = "inputEdgeType",
  outputTypeField = "outputEdgeType",
  inputLabelField = "inputLabel",
  outputLabelField = "outputLabel",
  inputFilterField = "inputEdgeType",
  outputFilterField = "outputEdgeType",
  includeMovementControls = true,
}: DiscipleshipPeopleMovementTabProps) {
  const navigate = useNavigate();

  const [people, setPeople] = useState<Person[]>([]);
  const [selectedPeople, setSelectedPeople] = useState<Person[]>([]);
  const [selectedInputID, setSelectedInput] = useState<string>("");
  const [selectedOutputID, setSelectedOutput] = useState<string>("");

  const [moving, setMoving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDirection, setPendingDirection] =
    useState<"forward" | "backward" | null>(null);

  const refreshPeople = useCallback(async () => {
    if (!nodeData?.id) return;
    const refreshed = await getNodePeople(Number(nodeData.id));
    setPeople(refreshed);
    setSelectedPeople([]);
  }, [getNodePeople, nodeData?.id]);

  useEffect(() => {
    refreshPeople();
  }, [refreshPeople]);

  if (!nodeData) {
    return <Typography>Select a node to edit</Typography>;
  }

  const functionalities = nodeData?.data?.[nameOfFunctionalityField] || [];

  const inputFunctionalities = functionalities.filter(
    (f: any) =>
      f[inputFilterField] !== null &&
      f[inputFilterField] !== undefined &&
      f["inputEdgeType"] === 1
  );

  const outputFunctionalities = functionalities.filter(
    (f: any) =>
      f[outputFilterField] !== null &&
      f[outputFilterField] !== undefined &&
      f["outputEdgeType"] === 1
  );

  const handleMove = (direction: "forward" | "backward") => {
    if (moving) return;
    if (selectedPeople.length === 0) return;

    const targetID =
      direction === "forward" ? selectedOutputID : selectedInputID;

    if (!targetID) return;

    setPendingDirection(direction);
    setConfirmOpen(true);
  };

  const confirmMove = async () => {
    if (!pendingDirection) return;

    setConfirmOpen(false);
    setMoving(true);

    try {
      const targetID =
        pendingDirection === "forward"
          ? selectedOutputID
          : selectedInputID;

      await movePeopleBetweenNodes({
        node_id: Number(nodeData.id),
        handle_name: targetID,
        direction: pendingDirection,
        item_field_name: "people",
        selected_items: selectedPeople,
      });

      await refreshPeople();
    } catch (error) {
      console.error("Failed to move people:", error);
    } finally {
      setMoving(false);
      setPendingDirection(null);
    }
  };

  const cancelMove = () => {
    setConfirmOpen(false);
    setPendingDirection(null);
  };

  const columns: Column<any>[] = [
    { field: "first_name", label: "First Name", type: "text" },
    { field: "last_name", label: "Last Name", type: "text" },
    { field: "preferred_name", label: "Preferred Name", type: "text" },
    { field: "email", label: "Email", type: "text" },
    { field: "phone", label: "Phone", type: "text" },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {includeMovementControls && (
        <DiscipleshipPeopleControlPanel
          inputFunctionalities={inputFunctionalities}
          outputFunctionalities={outputFunctionalities}
          selectedInputID={selectedInputID}
          selectedOutputID={selectedOutputID}
          setSelectedInput={setSelectedInput}
          setSelectedOutput={setSelectedOutput}
          handleMove={handleMove}
          nodeData={nodeData}
          items={people}
          selectedItems={selectedPeople}
          refreshItems={refreshPeople}
          inputTypeField={inputTypeField}
          outputTypeField={outputTypeField}
          inputLabelField={inputLabelField}
          outputLabelField={outputLabelField}
          disabled={moving}
        />
      )}

      <GenericTable
        data={people}
        columns={columns}
        selectable={includeMovementControls && !moving}
        onSelectionChange={(rows) => setSelectedPeople(rows)}
        linkHandler={() => navigate("/community/people/")}
        includePaper={false}
      />

      <MoveConfirmDialog
        open={confirmOpen}
        onConfirm={confirmMove}
        onCancel={cancelMove}
      />
    </Box>
  );
}